/* eslint-disable no-param-reassign */
import axios from 'axios';

import HttpStatus from 'http-status-codes';

import {
  CLIENT_ID,
  CLIENT_SECRET,
  IDENTITY_URL
} from '../utils/constants';
import LocalUserService from './logic/userService';

let refreshing = false;
let subscribers = [];

function onAccessTokenFetched(accessToken) {
  subscribers.forEach((callback) => callback(accessToken));
  subscribers = [];
}

async function request(url, method, payload, optionalHeaders, options) {
  const axiosConfig = {
    method,
    url,
    data: payload || null,
    headers: {
      'Content-Type': 'application/json'
    },
    ...options,
  };
  
  const user = await LocalUserService.getUser();
  let refreshToken = user ? user.refreshToken || null : null;
  
  const refreshAuthLogic = async (error) => {
    const {
      response: errorResponse
    } = error;

    const retryOriginalRequest = new Promise((resolve) => {
      subscribers.push((accessToken) => {
        errorResponse.config.headers.Authorization = `Bearer ${accessToken}`;
        resolve(axios(errorResponse.config));
      });
    });

    if (!refreshing) {
      refreshing = true;

      try {
        const refreshAxios = axios.create();
        const params = new URLSearchParams({
          client_id: CLIENT_ID,
          client_secret: CLIENT_SECRET,
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        });
        
        const refreshResponse = await refreshAxios.post(`${IDENTITY_URL}/connect/token`, params);
        
        if (refreshResponse.status === HttpStatus.OK) {
          const { 
            access_token,
            refresh_token
          } = refreshResponse.data;

          LocalUserService.setAccessToken(access_token);
          LocalUserService.setRefreshToken(refresh_token);
          onAccessTokenFetched(access_token);
        } 
        else {
          throw new Error('Failed to refresh token');
        }
      } 
      catch (refreshError) {
        LocalUserService.logout();
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } 
      finally {
        refreshing = false;
      }
    }

    return retryOriginalRequest;
  };

  const axiosInstance = axios.create();

  axiosInstance.interceptors.response.use(
    (response) => response, async (error) => {
      const errorResponse = error.response;
      const errorResponseData = handleErrors(error);
      const parsedErrors = parseErrors(errorResponseData);

      window.ErrorsContainer.setErrors(parsedErrors);

      if (errorResponse && errorResponse.status === HttpStatus.UNAUTHORIZED) {
        return refreshAuthLogic(error);
      }

      if (errorResponse?.status === HttpStatus.FORBIDDEN) {
        LocalUserService.logout();
        window.location.href = '/login';

        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.request.use((config) => {
    const token = user?.accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (optionalHeaders) {
      Object.keys(optionalHeaders)
        .forEach((key) => {
          config.headers[key] = optionalHeaders[key];
        });
    }
    
    return config;
  });

  return axiosInstance.request(axiosConfig);
}

function handleErrors(error) {
  const errorResponse = error.response;
  const errorMessage = error.message;

  if (errorResponse) {
    if (errorResponse.status !== 401 && errorResponse.data) {
      if (errorResponse.data.errors) {
        return errorResponse.data.errors;
      }

      return errorResponse.data;
    }

    return false;
  }
  if (errorMessage) {
    const newError = {
      Error: errorMessage
    };

    return newError;
  }

  return error;
}

function parseErrors(errors) {
  if (errors) {
    const errorsToArr = [];

    if (typeof errors === 'object' && errors.constructor === Object) {
      Object.keys(errors)
        .forEach((key) => {
          errorsToArr.push({
            key,
            data: errors[key]
          });
        });

      return errorsToArr;
    }
    errorsToArr.push({
      data: errors
    });

    return errorsToArr;
  }

  return false;
}

export default request;
